import { useCallback, useEffect, useState } from 'react';
import DrawingToolbar, { ToolKey } from './DrawingToolbar';
import DisplayTimer from './DisplayTimer';
import PencilSizeCard, { PENCIL_MAX, PENCIL_MIN } from './PencilSizeCard';
import PencilColourCard from './PencilColourCard';
import ToolsShapes from './ToolsShapes';
import ToolsFill from './ToolsFill';
import ToolsGrid from './ToolsGrid';
import SaveImageCard from './SaveImageCard';
import SaveModal from './SaveModal';
import CanvasPreview from './CanvasPreview';
import Card from './ui/Card';
import { GoldButton } from './ui/Buttons';
import { PanelHint } from './ui/Panel';
import { useColorHistoryContext } from '../contexts/ColorHistoryContext';
import { saveOnlineAndClose } from '../helpers/saveOnline';

// What a tap on the canvas does. Colour, grid and save are settings and
// never change it.
type Mode = 'draw' | 'move' | 'shapes' | 'fill';
// Every tool opens a pop-up card (GUI "All tools use a pop-up dialogue box"),
// except Move canvas, which is a toggle.
type CardKey = 'size' | 'colour' | 'background' | 'shapes' | 'fill' | 'grid' | 'save' | 'saveDevice' | 'preview';

const MODE_TOOL: Record<Mode, ToolKey> = { draw: 'size', move: 'move', shapes: 'shapes', fill: 'fill' };

export type ToolsProps = {
	/** When set, the pencil size card opens first as the last setup step */
	initialPencilStep?: { step: number; totalSteps: number } | null;
};

function Tools({ initialPencilStep }: ToolsProps) {
	const p5 = useCallback(() => window.editSketch, []); // p5 instance of editSketch
	const [mode, setMode] = useState<Mode>('draw');
	// The colour card can open on top of the shapes or fill card, so keep a stack
	const [cards, setCards] = useState<CardKey[]>(initialPencilStep ? ['size'] : []);
	const [pencilStep, setPencilStep] = useState(initialPencilStep ?? null);
	const [onlineSaving, setOnlineSaving] = useState(false);
	const [pencilHex, setPencilHex] = useState('#FFFFFFFF');
	const [backgroundHex, setBackgroundHex] = useState('#000000FF');
	const [pencilSize, setPencilSize] = useState({ x: 1, y: 1 });

	const { colorHistory, selectColorFromHistory } = useColorHistoryContext();

	const isOpen = (card: CardKey) => cards.includes(card);
	const openCard = (card: CardKey) => setCards(stack => (stack.includes(card) ? stack : [...stack, card]));
	const closeCard = (card: CardKey) => setCards(stack => stack.filter(c => c !== card));

	// Read the sketch's current pencil/background settings into React state
	const syncFromSketch = useCallback(() => {
		const sketch = p5();
		if (!sketch) return;
		const pencil = sketch.getPencilColourHex?.();
		if (pencil) setPencilHex(pencil);
		const bg = sketch.getBackgroundColourHex?.();
		if (bg) setBackgroundHex(bg);
		const size = sketch.getPensilSize?.();
		if (size) setPencilSize({ x: size.x, y: size.y });
	}, [p5]);

	useEffect(() => {
		syncFromSketch();
		// The sketch finishes setup a moment after mount
		const t = setTimeout(syncFromSketch, 500);
		return () => clearTimeout(t);
	}, [syncFromSketch]);

	const switchMode = (next: Mode) => {
		const sketch = p5();
		if (mode === 'fill' && next !== 'fill') sketch?.setBucketActive?.(false);
		if (mode === 'shapes' && next !== 'shapes') sketch?.setShapeActive?.(false);
		// Hand (move) mode is a toggle on the sketch
		if ((next === 'move') !== (mode === 'move')) sketch?.toggleMode?.();
		if (next === 'fill' && mode !== 'fill') sketch?.setBucketActive?.(true);
		if (next === 'shapes' && mode !== 'shapes') sketch?.setShapeActive?.(true);
		setMode(next);
	};

	const handleSelect = (tool: ToolKey) => {
		syncFromSketch();
		switch (tool) {
			case 'size':
				switchMode('draw');
				openCard('size');
				break;
			case 'colour':
				// Picking a colour while moving the canvas means you want to draw next
				if (mode === 'move') switchMode('draw');
				openCard('colour');
				break;
			case 'move':
				switchMode(mode === 'move' ? 'draw' : 'move');
				break;
			case 'shapes':
				switchMode('shapes');
				openCard('shapes');
				break;
			case 'fill':
				switchMode('fill');
				openCard('fill');
				break;
			case 'grid':
				openCard('grid');
				break;
			case 'save':
				openCard('save');
				break;
		}
	};

	const isActive = (tool: ToolKey) => {
		if (tool === 'colour') return isOpen('colour');
		if (tool === 'grid') return isOpen('grid');
		if (tool === 'save') return isOpen('save') || isOpen('saveDevice') || isOpen('preview');
		return MODE_TOOL[mode] === tool;
	};

	const setPencilColour = (hexa: string) => {
		p5()?.setPencilColour(hexa);
		setPencilHex(hexa);
	};

	const setBackgroundColour = (hexa: string) => {
		p5()?.setBackgroundColour(hexa);
		setBackgroundHex(hexa);
	};

	const handleRecentSelect = (hex: string) => {
		setPencilColour(hex);
		selectColorFromHistory(hex); // move it to the front of the history
	};

	const updatePencilSize = (x: number, y: number) => {
		const clamp = (n: number) => Math.min(PENCIL_MAX, Math.max(PENCIL_MIN, n));
		const next = { x: clamp(x), y: clamp(y) };
		p5()?.setPensilSize(next.x, next.y);
		setPencilSize(next);
	};

	const closeSize = () => {
		closeCard('size');
		setPencilStep(null);
	};

	const handleSaveOnline = async () => {
		if (onlineSaving) return;
		setOnlineSaving(true);
		try {
			await saveOnlineAndClose();
		} catch (error: any) {
			console.error(error);
			alert(`Error saving image: \n ${error?.message ?? error}`);
		} finally {
			setOnlineSaving(false);
		}
	};

	const colourTarget = isOpen('background') ? 'background' : isOpen('colour') ? 'pencil' : null;

	return (
		<>
			<div style={{ marginTop: 12 }}>
				<DrawingToolbar isActive={isActive} onSelect={handleSelect} />
			</div>

			<DisplayTimer />

			<PencilSizeCard
				open={isOpen('size')}
				width={pencilSize.x}
				height={pencilSize.y}
				onChange={updatePencilSize}
				onClose={closeSize}
				step={pencilStep?.step}
				totalSteps={pencilStep?.totalSteps}
			/>

			<Card open={isOpen('shapes')} title="Shapes" onClose={() => closeCard('shapes')}>
				<ToolsShapes p5={p5} pencilHex={pencilHex} pencilSize={pencilSize} onEditColour={() => openCard('colour')} onEditSize={() => openCard('size')} />
				<GoldButton type="button" onClick={() => closeCard('shapes')} style={{ width: '100%', marginTop: 24 }}>
					Start drawing
				</GoldButton>
			</Card>

			<Card open={isOpen('fill')} title="Fill" onClose={() => closeCard('fill')}>
				<ToolsFill p5={p5} pencilHex={pencilHex} backgroundHex={backgroundHex} onEditColour={() => openCard('colour')} onEditBackground={() => openCard('background')} />
				<GoldButton type="button" onClick={() => closeCard('fill')} style={{ width: '100%', marginTop: 24 }}>
					Start filling
				</GoldButton>
			</Card>

			<Card open={isOpen('grid')} title="Grid" onClose={() => closeCard('grid')}>
				<PanelHint style={{ margin: '16px 0 14px' }}>Show guide lines over your canvas while you draw.</PanelHint>
				<ToolsGrid />
				<GoldButton type="button" onClick={() => closeCard('grid')} style={{ width: '100%', marginTop: 24 }}>
					Done
				</GoldButton>
			</Card>

			<SaveImageCard
				open={isOpen('save')}
				onClose={() => closeCard('save')}
				onLocalDevice={() => {
					closeCard('save');
					openCard('saveDevice');
				}}
				onOnline={handleSaveOnline}
				onPreview={() => {
					closeCard('save');
					openCard('preview');
				}}
				onlineSaving={onlineSaving}
			/>
			<SaveModal open={isOpen('saveDevice')} onClose={() => closeCard('saveDevice')} />
			<CanvasPreview open={isOpen('preview')} onClose={() => closeCard('preview')} />

			<PencilColourCard
				open={colourTarget !== null}
				title={colourTarget === 'background' ? 'Canvas background colour' : 'Specify pencil colour'}
				color={colourTarget === 'background' ? backgroundHex : pencilHex}
				onChange={colourTarget === 'background' ? setBackgroundColour : setPencilColour}
				onClose={() => closeCard(colourTarget === 'background' ? 'background' : 'colour')}
				recentColors={colourTarget === 'pencil' ? colorHistory : undefined}
				onRecentSelect={handleRecentSelect}
			/>
		</>
	);
}

export default Tools;
